<template>
  <!-- right side -->
  <div class="w-full flex items-center justify-center">
    <div
      class="flex flex-col bg-white w-full md:w-[50%] xl:w-[30%] px-6 py-2 items-center space-y-1 border border-slate-200 shadow-lg rounded-lg h-auto"
    >
      <p class="text-subDark font-bold text-xl">{{ $t("welcome-back") }}</p>
      <p class="text-slate-500 font-medium text-sm">
        {{ $t("enter-your-email-to-sign-in-to-your-account") }}
      </p>
      <div class="relative w-full flex flex-col space-y-1">
        <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
          {{ $t("Email") }}
        </p>
        <input
          v-model="email"
          type="text"
          :class="{ 'pr-3': locale === 'ar' }"
          class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
          :placeholder="$t('Email')"
          @keydown.enter.exact.prevent="login"
          required
        />
      </div>
      <div class="relative flex flex-col w-full space-y-2 pt-2">
        <div class="flex flex-row justify-between">
          <p class="text-slate-900 font-semibold text-sm">
            {{ $t("Password") }}
          </p>
          <router-link to="/reset" class="text-sm font-medium text-[#4867C8]">
            {{ $t("Forgot_your_password") }}
          </router-link>
        </div>
        <div class="relative w-full">
          <input
            v-model="password"
            :type="viewPassword ? 'text' : 'password'"
            :class="{ 'pr-3': locale === 'ar' }"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('Password')"
            @keydown.enter.exact.prevent="login"
            required
          />
          <button
            type="button"
            :class="{ 'right-2': locale != 'ar', 'left-2': locale === 'ar' }"
            class="absolute inset-y-0 flex items-center pr-3 text-gray-600"
            @click="togglePassword"
          >
            <svg
              v-if="viewPassword"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                d="m17.069 6.546 2.684-2.359c.143-.125.32-.187.497-.187.418 0 .75.34.75.75 0 .207-.086.414-.254.562l-16.5 14.501c-.142.126-.319.187-.496.187-.415 0-.75-.334-.75-.75 0-.207.086-.414.253-.562l2.438-2.143c-1.414-1.132-2.627-2.552-3.547-4.028-.096-.159-.144-.338-.144-.517s.049-.358.145-.517c2.111-3.39 5.775-6.483 9.853-6.483 1.815 0 3.536.593 5.071 1.546zm2.319 1.83c.966.943 1.803 2.014 2.474 3.117.092.156.138.332.138.507s-.046.351-.138.507c-2.068 3.403-5.721 6.493-9.864 6.493-1.297 0-2.553-.313-3.729-.849l1.247-1.096c.795.285 1.626.445 2.482.445 3.516 0 6.576-2.622 8.413-5.5-.595-.932-1.318-1.838-2.145-2.637zm-3.434 3.019c.03.197.046.399.046.605 0 2.208-1.792 4-4 4-.384 0-.756-.054-1.107-.156l1.58-1.389c.895-.171 1.621-.821 1.901-1.671zm-.058-3.818c-1.197-.67-2.512-1.077-3.898-1.077-3.465 0-6.533 2.632-8.404 5.5.853 1.308 1.955 2.567 3.231 3.549l1.728-1.519c-.351-.595-.553-1.289-.553-2.03 0-2.208 1.792-4 4-4 .925 0 1.777.315 2.455.843zm-2.6 2.285c-.378-.23-.822-.362-1.296-.362-1.38 0-2.5 1.12-2.5 2.5 0 .36.076.701.213 1.011z"
                fill-rule="nonzero"
              />
            </svg>
            <svg
              v-else
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                d="m11.998 5c-4.078 0-7.742 3.093-9.853 6.483-.096.159-.145.338-.145.517s.048.358.144.517c2.112 3.39 5.776 6.483 9.854 6.483 4.143 0 7.796-3.09 9.864-6.493.092-.156.138-.332.138-.507s-.046-.351-.138-.507c-2.068-3.403-5.721-6.493-9.864-6.493zm8.413 7c-1.837 2.878-4.897 5.5-8.413 5.5-3.465 0-6.532-2.632-8.404-5.5 1.871-2.868 4.939-5.5 8.404-5.5 3.518 0 6.579 2.624 8.413 5.5zm-8.411-4c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4zm0 1.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"
                fill-rule="nonzero"
              />
            </svg>
          </button>
        </div>
      </div>
      <p v-if="hasError" class="text-lg font-semibold text-red-600">
        {{ errorMessage }}
      </p>
      <div class="pt-2"></div>
      <button
        @click="login"
        class="bg-subDark text-white font-semibold py-1 px-12 rounded-lg w-full h-10"
      >
        {{ $t("Login") }}
      </button>
      <p class="text-base text-center text-slate-500 font-medium py-2">
        {{ $t("OR") }}
      </p>
      <button
        @click="googleSignin"
        class="bg-white border border-slate-300 w-full rounded-md text-[#061B44] font-semibold flex flex-row items-center justify-center h-10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="35"
          height="25"
          viewBox="1325.916 305 37.551 38.316"
        >
          <g data-name="Groupe 5578">
            <g data-name="Google__G__logo">
              <path
                d="M1363.466 324.594c0-1.359-.122-2.665-.348-3.92h-18.044v7.42h10.31c-.452 2.386-1.81 4.407-3.848 5.765v4.825h6.217c3.623-3.344 5.713-8.256 5.713-14.09Z"
                fill="#4285f4"
                fill-rule="evenodd"
                data-name="Tracé 9724"
              />
              <path
                d="M1345.074 343.316c5.173 0 9.51-1.706 12.68-4.632l-6.218-4.825c-1.707 1.15-3.884 1.846-6.462 1.846-4.98 0-9.213-3.361-10.728-7.89h-6.375v4.947c3.153 6.253 9.614 10.554 17.103 10.554Z"
                fill="#34a853"
                fill-rule="evenodd"
                data-name="Tracé 9725"
              />
              <path
                d="M1334.346 327.798a11.48 11.48 0 0 1-.61-3.64c0-1.271.226-2.49.61-3.64v-4.946h-6.375a18.922 18.922 0 0 0-2.055 8.586c0 3.1.749 6.009 2.055 8.587l4.964-3.867 1.41-1.08Z"
                fill="#fbbc05"
                fill-rule="evenodd"
                data-name="Tracé 9726"
              />
              <path
                d="M1345.074 312.628c2.822 0 5.33.976 7.333 2.857l5.486-5.486c-3.327-3.1-7.646-4.999-12.819-4.999-7.489 0-13.95 4.302-17.103 10.572l6.375 4.946c1.515-4.528 5.747-7.89 10.728-7.89Z"
                fill="#ea4335"
                fill-rule="evenodd"
                data-name="Tracé 9727"
              />
            </g>
          </g>
        </svg>
        <p class="text-slate-700 font-medium px-2">
          {{ $t("Login_using_google") }}
        </p>
      </button>
      <!-- <button
          class="bg-white text-[#061B44] font-semibold py-4 pl-20 rounded-full w-[45%] flex items-start justify-start"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="35"
            height="25"
            viewBox="1326.668 486 36.813 43.704"
          >
            <path
              d="M1362.709 520.058a23.76 23.76 0 0 1-2.35 4.225c-1.235 1.762-2.247 2.98-3.027 3.658-1.208 1.111-2.503 1.68-3.89 1.713-.995 0-2.196-.283-3.593-.858-1.402-.572-2.69-.855-3.868-.855-1.236 0-2.561.283-3.978.855-1.42.575-2.563.874-3.438.904-1.33.057-2.655-.529-3.978-1.759-.844-.736-1.9-1.999-3.165-3.787-1.358-1.91-2.474-4.125-3.348-6.65-.937-2.728-1.406-5.369-1.406-7.926 0-2.928.633-5.454 1.9-7.571.997-1.7 2.322-3.042 3.98-4.027a10.708 10.708 0 0 1 5.382-1.518c1.056 0 2.441.327 4.162.969 1.717.644 2.819.97 3.302.97.361 0 1.585-.381 3.66-1.143 1.963-.706 3.62-.999 4.977-.884 3.676.297 6.439 1.747 8.276 4.358-3.288 1.993-4.915 4.783-4.883 8.363.03 2.789 1.041 5.11 3.03 6.952a9.955 9.955 0 0 0 3.026 1.986c-.242.704-.499 1.378-.771 2.025Zm-8.433-33.184c0 2.186-.799 4.227-2.39 6.116-1.92 2.245-4.244 3.543-6.764 3.338a6.804 6.804 0 0 1-.05-.828c0-2.098.913-4.344 2.535-6.18.81-.93 1.84-1.703 3.09-2.32 1.246-.607 2.424-.942 3.533-1 .033.292.046.584.046.874Z"
              fill-rule="evenodd"
              data-name="Apple_logo_grey"
            />
          </svg>
          Login using Apple
        </button> -->
      <div class="flex flex-col">
        <p class="text-xs pt-2 text-center text-slate-500 font-medium">
          {{ $t("By_Proceeding_you_agree") }}
        </p>
        <div
          class="text-xs underline underline-offset-4 pb-2 text-center text-slate-700 font-medium mt-2 flex flex-row"
        >
          <p>{{ $t("toour") }}</p>
          &nbsp;
          <a href="/terms">{{ $t("Termsofuse") }}</a>
          &nbsp;
          <p>{{ $t("and") }}</p>
          &nbsp;
          <a href="/privacy-policy"> {{ $t("privacy-policy") }}</a>
        </div>
      </div>
      <p class="text-sm md:text-base font-medium text-slate-600">
        {{ $t("Dont_have_an_account_yet") }}
        <a class="text-[#4867C8]" href="/signing-up">{{ $t("Sign_up") }}</a>
      </p>
    </div>
  </div>
  <!-- left side -->
  <!-- <div
      class="flex flex-col bg-white lg:w-[50%] items-center justify-center py-10 lg:py-0"
    >
      <img
        src="../assets/images/auth/01.png"
        alt=""
        class="w-[60%] lg:w-[40%]"
      />
    </div> -->
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../store/auth";
import {
  API_BASE_URL,
  GETNOTIFICATION,
  GOOGLEAUTH,
  SIGNIN,
} from "../utils/appConst";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useUserProfile } from "../store/userProfile";
import { useI18n } from "vue-i18n";
import { googleTokenLogin } from "vue3-google-login";
import Cookies from "js-cookie";
import { useNotificationStore } from "../store/notification";
import { NotificationStatus } from "../utils/notificationModel";

const router = useRouter();
const email = ref("");
const password = ref("");
const loginFailed = ref(false);
const hasError = ref(false);
const viewPassword = ref(false);
const errorMessage = ref("");
let userToken = "";
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const loginUrl = `${API_BASE_URL}${SIGNIN}`;
const googleAuthUrl = `${API_BASE_URL}${GOOGLEAUTH}`;
const getNotificationUrl = `${API_BASE_URL}${GETNOTIFICATION}`;
const userStore = useUserProfile();
const { t, locale } = useI18n();
const showAuthPop = computed(() => authStore.showAuthPop);
let isLoading = false;

const togglePassword = () => {
  viewPassword.value = !viewPassword.value;
};

const login = async () => {
  if (isLoading) {
    return;
  }
  loginFailed.value = false;
  hasError.value = false;
  if (email.value.trim().length == 0 || password.value.length == 0) {
    /* errorMessage.value = t(
      "email-and-password-are-required-fields-please-fill-them-in-thanks"
    ); */
    toast(
      t("email-and-password-are-required-fields-please-fill-them-in-thanks"),
      {
        autoClose: 2500,
        type: "error",
        theme: "light",
      }
    );
    //hasError.value = true;
    return;
  }
  if (!isValidEmail(email.value)) {
    //errorMessage.value = t("oops-invalid-email-please-check-and-retry-thanks");
    toast(t("oops-invalid-email-please-check-and-retry-thanks"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
    //hasError.value = true;
    return;
  }
  isLoading = true;
  let resolveLogin: (value?: unknown) => void;
  let rejectLogin: (reason?: any) => void;
  try {
    const loginPromise = new Promise(async (resolve, reject) => {
      resolveLogin = resolve;
      rejectLogin = reject;
      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          email: email.value,
          password: password.value,
          fcmToken: authStore.fcmToken,
        }),
      });
      const result = await response.json();

      userToken = result.token;
      if (!userToken) {
        loginFailed.value = true;
        rejectLogin("Authentication failed");
        return;
      }

      resolveLogin();
    });

    toast.promise(
      loginPromise,
      {
        pending: t("logging-in"),
        success: t("login-successful"),
        error: t("authentication-failed"),
      },
      {
        position: toast.POSITION.TOP_CENTER,
        autoClose: true,
        closeOnClick: true,
        theme: "light",
      }
    );

    loginPromise
      .then(async () => {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        authStore.setToken(userToken);
        authStore.setUsingGoogleAuth(false);
        await authStore.getUnseenCount();
        await userStore.getUserSubscription(authStore.token!);
        await updateNotification();
        if (authStore.showAuthPop) {
          authStore.setAuthPop(false);
        } else {
          router.push("/");
        }
      })
      .catch((error) => {
        rejectLogin("Authentication failed");
        console.error("Authentication failed:", error);
        isLoading = false;
      });
  } catch (error) {
    console.error("Authentication failed:", error);
    isLoading = false;
  }
};

function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

const googleSignin = () => {
  googleTokenLogin().then(async (response) => {
    if (response.access_token) {
      toast(t("logging-in"), {
        autoClose: 2500,
        type: "info",
        theme: "light",
      });
      const reqResponse = await fetch(googleAuthUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          credential: response.access_token,
          userFcmToken: authStore.fcmToken,
        }),
      });

      const result = await reqResponse.json();

      if (result.error) {
        toast(t("authentication-failed"), {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
        return;
      }
      if (result.token) {
        toast(t("sign-up-successful"), {
          autoClose: 2500,
          type: "success",
          theme: "light",
        });
        userToken = result.token;
        authStore.setToken(userToken);
        authStore.setUsingGoogleAuth(true);
        await authStore.getUnseenCount();
        await userStore.getUserSubscription(authStore.token!);
        await updateNotification();
        if (authStore.showAuthPop) {
          authStore.setAuthPop(false);
        } else {
          router.push("/");
        }
      }
    }
  });
};

const updateNotification = async () => {
  try {
    const getData = await fetch(`${getNotificationUrl}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ page: 1 }),
    });

    if (!getData.ok) {
      throw new Error(`HTTP error! Status: ${getData.status}`);
    }

    const { notifications } = await getData.json();
    notificationStore.dropNotificationList = notifications.map(
      (notification: any) => ({
        id: notification.notificationId,
        message: notification.message || null,
        type: notification.type,
        seen: notification.seen,
        createdAt: new Date(notification.createdDate),
        originalLang: notification.originalLang,
        targetLang: notification.targetLang,
        requestId: notification.requestId || null,
        relatedRequestId: notification.relatedRequestId || null,
        videoLink:
          notification.youtubeVideoId || notification.videoLink || null,
        audioLink: notification.audioLink || null,
        fileName: notification.videoTitle || notification.audioTitle || null,
        status:
          (notification.status as NotificationStatus) ||
          NotificationStatus.SUCCESS,
        uploadAlreadyUsed: notification.uploadAlreadyUsed === true,
        consumed: notification.consumed === true,
        orderSourceType: notification.orderSourceType ?? null,
      })
    );
    notificationStore.dropNotificationList.splice(6);
    authStore.getUnseenCount();
  } catch (error) {
    console.log("getNotification: ", error);
  }
};

const navigate = (path: string) => {
  router.push(`/${path}`);
};
</script>
